import vuex from '@/store'

/**
 * Resolves a station identifier to a numeric station id for the
 * ?filter[station_id][]=<id> API convention (ND-1490).
 *
 * Accepts an id (returned as-is), a station name string (looked up against
 * the full station list already loaded into Vuex), a station object
 * ({ id, ... } or { station_name, ... }), or nothing (falls back to the
 * current station). Needed because a handful of call sites pass an explicit,
 * non-current station — usually a name, since that's what the old
 * `{station_name}` path convention required — rather than always reading
 * `currentStation` directly.
 *
 * Throws when nothing resolves. The backend drops a missing or non-numeric
 * filter[station_id][] and continues with no station context, so silently
 * substituting the current station (or sending nothing) would read from or
 * write to the wrong workspace instead of failing.
 */
export default function resolveStationId(stationNameOrIdOrObject) {
  const value = stationNameOrIdOrObject

  if (value == null || value === '') {
    const currentId = vuex.getters.currentStation?.id
    if (currentId == null) throw new Error('No current station id available')
    return currentId
  }

  if (typeof value === 'object') {
    if (value.id != null) return value.id
    return resolveStationId(value.station_name ?? value.station)
  }

  if (typeof value === 'number' || /^\d+$/.test(String(value))) {
    return value
  }

  const match = (vuex.getters.stationConfigs ?? []).find((s) => s.station_name?.toLowerCase() === String(value).toLowerCase())
  if (match?.id == null) throw new Error(`Unknown station "${value}"`)
  return match.id
}
